/**
 * Projekt als Popup (PV-052 L5, A11 „Projekt öffnen“). Der Projektteil der Befund-Zeile öffnet das
 * Projektdetail in einem breiten <Modal>. Die Liste darunter bleibt gemountet, der Hintergrund ist
 * gesperrt (Scroll-Sperre des <Modal>) — nach dem Schließen steht sie an derselben Stelle.
 *
 * Genau EIN Popup für die ganze Shell (Provider in App.tsx): Läge das Modal in der Befund-Zeile,
 * verschwände es mit ihr — etwa wenn das Karten-Popover schließt oder der Befund nach einer
 * Aktion aus der Liste fällt.
 */
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ProjektDetail } from '../../../pages/v2/project-detail.js';
import { Modal } from '../modal.js';
import { ProjektPopupKontext } from './projekt-popup-kontext.js';

export function ProjektPopupProvider({ children }: { children: ReactNode }) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  const schliessen = useCallback(() => {
    setProjectId(null);
    // Im Popup kann alles geändert worden sein (Phase, Befunde, Besetzung, Flags). Die Listen
    // darunter tragen je Seite eigene Keys (`v2-board-projects`, `v2-kfml-worklist`, …) — also
    // alle aktiven Abfragen auffrischen. Die Daten bleiben beim Nachladen stehen.
    void queryClient.invalidateQueries();
  }, [queryClient]);

  // Navigation (Kommandopalette, „Als Seite öffnen") schließt das Popup — über denselben
  // `schliessen`-Pfad wie der Knopf, sonst zeigt ein Zurück zur Liste innerhalb der
  // 30s-staleTime veraltete Daten (die Listen wurden ja nie aufgefrischt).
  const letzterPfad = useRef(pathname);
  useEffect(() => {
    if (letzterPfad.current === pathname) return;
    letzterPfad.current = pathname;
    if (projectId !== null) schliessen();
  }, [pathname, projectId, schliessen]);

  const wert = useMemo(() => ({ oeffne: (id: string) => setProjectId(id) }), []);
  return (
    <ProjektPopupKontext.Provider value={wert}>
      {children}
      {projectId && <ProjektDetailModal projectId={projectId} onClose={schliessen} />}
    </ProjektPopupKontext.Provider>
  );
}

export function ProjektDetailModal({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      label={t('v2.befund.popup_label', 'Projekt')}
      // Das Projektdetail hält Eingaben (Kommentar, Aufgaben, Staffing) — ein Fehlklick daneben
      // oder Escape darf sie nicht verwerfen. Geschlossen wird über „Schließen".
      closeOnBackdrop={false}
      closeOnEscape={false}
      // Feste Höhe, innen scrollt nur der Inhalt: Das <Modal> zentriert vertikal — mit
      // wechselnder Inhaltshöhe (Tabwechsel, Aufklappen) wanderten sonst oberer und unterer Rand
      // bei jedem Klick (Nutzertest 2026-09-19). 86vh − 2rem passt in die Fläche des <Modal>
      // (100vh abzüglich pt-4 und pb-[14vh]), das Overlay selbst scrollt also nie.
      panelClassName="w-full max-w-[1400px] h-[calc(86vh-2rem)] flex flex-col overflow-hidden"
    >
      <div
        className="shrink-0 flex items-center justify-end gap-3 px-4 py-2 border-b"
        style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
      >
        <Link
          to={`/projects/${projectId}`}
          className="text-sm hover:underline"
          style={{ color: 'var(--ink-2)' }}
        >
          {t('v2.befund.popup_als_seite', 'Als Seite öffnen')}
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="text-sm px-2.5 py-1 rounded-md border"
          style={{
            background: 'var(--surface)',
            borderColor: 'var(--line-soft)',
            color: 'var(--ink-2)',
          }}
        >
          {t('v2.befund.popup_schliessen', 'Schließen')}
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4">
        <ProjektDetail projectId={projectId} eingebettet />
      </div>
    </Modal>
  );
}
